import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, AlertCircle, Wand2 } from "lucide-react";
import { useArticles } from "@/hooks/useArticles";
import { generateSEOSuggestions, calculateReadTime } from "@/utils/seoOptimizer";
import { ArticleImageUpload } from "./ArticleImageUpload";
import { RichTextEditor } from "./RichTextEditor";

interface AddArticleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AddArticleDialog = ({ open, onOpenChange }: AddArticleDialogProps) => {
  const { createArticle } = useArticles();
  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    content: "",
    image: "",
    author: "Équipe Château de Clémentine",
    category: "Conseils",
    // SEO Fields
    metaTitle: "",
    metaDescription: "",
    keywords: "",
    canonicalUrl: ""
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const autoFillSEO = () => {
    if (!formData.title || !formData.content) {
      return;
    }

    const suggestions = generateSEOSuggestions(formData.title, formData.content, formData.category);
    
    setFormData(prev => ({
      ...prev,
      metaTitle: suggestions.metaTitle,
      metaDescription: suggestions.metaDescription,
      keywords: suggestions.keywords,
      canonicalUrl: suggestions.canonicalUrl
    }));
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[àáâäã]/g, 'a')
      .replace(/[èéêë]/g, 'e')
      .replace(/[ìíîï]/g, 'i')
      .replace(/[òóôöõ]/g, 'o')
      .replace(/[ùúûü]/g, 'u')
      .replace(/[ç]/g, 'c')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.subtitle || !formData.content) {
      return;
    }

    try {
      const slug = generateSlug(formData.title);
      const readTime = Math.ceil(formData.content.split(' ').filter(word => word.length > 0).length / 200);

      await createArticle({
        title: formData.title,
        subtitle: formData.subtitle,
        content: formData.content,
        image_url: formData.image || "/default-article-image.webp",
        author: formData.author,
        category: formData.category,
        meta_title: formData.metaTitle || `${formData.title} | Château de Clémentine`,
        meta_description: formData.metaDescription || formData.subtitle,
        keywords: formData.keywords,
        canonical_url: formData.canonicalUrl || `https://chateaudeclementine.com/blog/${slug}`,
        slug,
        read_time: readTime
      });

      // Reset form
      setFormData({
        title: "",
        subtitle: "",
        content: "",
        image: "",
        author: "Équipe Château de Clémentine",
        category: "Conseils",
        metaTitle: "",
        metaDescription: "",
        keywords: "",
        canonicalUrl: ""
      });

      onOpenChange(false);
    } catch (error) {
      console.error('Erreur lors de la création de l\'article:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-display">Ajouter un Nouvel Article</DialogTitle>
        </DialogHeader>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-4">
          {/* Main Content - Left Column */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Contenu Principal</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm font-medium mb-2">Titre de l'article *</Label>
                  <Input 
                    placeholder="Titre accrocheur de votre article"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium mb-2">Sous-titre *</Label>
                  <Input 
                    placeholder="Sous-titre explicatif (apparaît dans les cartes)"
                    value={formData.subtitle}
                    onChange={(e) => handleInputChange('subtitle', e.target.value)}
                  />
                </div>

                <RichTextEditor
                  value={formData.content}
                  onChange={(value) => handleInputChange('content', value)}
                  rows={15}
                />

                <ArticleImageUpload
                  value={formData.image}
                  onChange={(value) => handleInputChange('image', value)}
                />

                <div>
                  <Label className="text-sm font-medium mb-2">Catégorie</Label>
                  <select 
                    className="w-full px-3 py-2 border border-border rounded-md bg-background"
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                  >
                    <option value="Conseils">Conseils</option>
                    <option value="Sécurité">Sécurité</option>
                    <option value="Actualités">Actualités</option>
                    <option value="Guide">Guide</option>
                    <option value="Événements">Événements</option>
                  </select>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* SEO Sidebar - Right Column */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <div className="flex items-center">
                    <AlertCircle className="w-4 h-4 mr-2 text-primary" />
                    Optimisation SEO
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={autoFillSEO}
                    disabled={!formData.title || !formData.content}
                    className="text-xs"
                  >
                    <Wand2 className="w-3 h-3 mr-1" />
                    Auto-compléter
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm font-medium mb-2">Méta Titre (SEO)</Label>
                  <Input 
                    placeholder="Titre optimisé pour Google (60 caractères max)"
                    value={formData.metaTitle}
                    onChange={(e) => handleInputChange('metaTitle', e.target.value)}
                    maxLength={60}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {formData.metaTitle.length}/60 caractères
                  </p>
                </div>

                <div>
                  <Label className="text-sm font-medium mb-2">Méta Description (SEO)</Label>
                  <Textarea 
                    placeholder="Description qui apparaît dans Google (160 caractères max)"
                    rows={3}
                    value={formData.metaDescription}
                    onChange={(e) => handleInputChange('metaDescription', e.target.value)}
                    maxLength={160}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {formData.metaDescription.length}/160 caractères
                  </p>
                </div>

                <div>
                  <Label className="text-sm font-medium mb-2">Mots-clés (SEO)</Label>
                  <Input 
                    placeholder="château gonflable, fête enfant, anniversaire"
                    value={formData.keywords}
                    onChange={(e) => handleInputChange('keywords', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Séparez les mots-clés par des virgules
                  </p>
                </div>

                <div>
                  <Label className="text-sm font-medium mb-2">URL Canonique</Label>
                  <Input 
                    placeholder="https://chateaudeclementine.com/blog/mon-article"
                    value={formData.canonicalUrl}
                    onChange={(e) => handleInputChange('canonicalUrl', e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* SEO Tips */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">💡 Conseils SEO</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-xs text-muted-foreground">
                  <p>• Utilisez des mots-clés pertinents dans le titre</p>
                  <p>• Rédigez une méta description attractive</p>
                  <p>• Incluez des mots-clés locaux (Rhode-Saint-Genèse)</p>
                  <p>• Minimum 300 mots pour un bon référencement</p>
                  <p>• Structurez votre contenu avec des paragraphes</p>
                </div>
              </CardContent>
            </Card>

            {/* Preview */}
            {formData.title && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Aperçu Google</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <h3 className="text-blue-600 text-sm font-medium cursor-pointer hover:underline">
                      {formData.metaTitle || formData.title}
                    </h3>
                    <p className="text-green-600 text-xs">
                      chateaudeclementine.com/blog/{generateSlug(formData.title)}
                    </p>
                    <p className="text-xs text-gray-700">
                      {formData.metaDescription || formData.subtitle}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        <div className="flex justify-between items-center p-4 border-t">
          <div className="text-sm text-muted-foreground">
            * Champs obligatoires
          </div>
          <div className="space-x-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={!formData.title || !formData.subtitle || !formData.content}
              variant="hero"
            >
              <Plus className="w-4 h-4 mr-2" />
              Publier l'Article
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};